/**
 * OIDC client helpers — Cognito Authorization Code + PKCE flow.
 *
 * Per AUTH-REDESIGN-directives.md:
 *   - PKCE code_verifier lives transiently in sessionStorage (verifier
 *     only — NEVER the token). Cleared after one-shot use in /callback.
 *   - state value carries CSRF protection.
 *   - No token of any kind is ever written to localStorage / sessionStorage
 *     / cookies (cookies are set server-side, httpOnly).
 *   - The post-login destination is held in `state` (validated against an
 *     in-memory allowlist on /callback), NOT in a free-form ?next= URL.
 *
 * Both code_verifier and state are bound to a single redirect attempt
 * (one entry in sessionStorage per attempt, keyed by state).
 */

const STATE_KEY_PREFIX = 'f2_oidc_';
const POOL_REGION = 'us-east-1';
const USER_POOL_ID = 'us-east-1_0Q00rhHoS';
// f2-admin-v2 app client — public (no secret), PKCE-only. Distinct from
// the scanner-side f2-scan-apps client (634pu1fk5atm01livrl3jicltf) which
// remains bound to legacy code paths (customer SSO, user-management). Same
// user pool — only the app-client identity differs.
const CLIENT_ID = '2vr6lla7dsjhc24l9oo91032ev';

/**
 * Hosted UI custom domain. The Cognito auto-provisioned prefix
 * (<prefix>.auth.<region>.amazoncognito.com) is NOT used — we run a
 * branded custom domain via ACM + CloudFront. TLD-neutral: the same
 * Cognito domain serves both .com and .ai users; only redirect_uri
 * differs (derived from window.location.origin).
 */
const COGNITO_HOSTED_UI = `https://auth.f2-tech.ai`;

function base64UrlEncode(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomBytes(n: number): Uint8Array {
  const buf = new Uint8Array(n);
  crypto.getRandomValues(buf);
  return buf;
}

async function sha256(input: string): Promise<Uint8Array> {
  const data = new TextEncoder().encode(input);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return new Uint8Array(hash);
}

export function generateCodeVerifier(): string {
  // 32 random bytes → 43-char base64url string (RFC 7636 recommends 32+ bytes).
  return base64UrlEncode(randomBytes(32));
}

export async function generateCodeChallenge(verifier: string): Promise<string> {
  return base64UrlEncode(await sha256(verifier));
}

export function generateStateValue(): string {
  // 16 random bytes → 22-char base64url string.
  return base64UrlEncode(randomBytes(16));
}

export interface PendingAuth {
  codeVerifier: string;
  redirectUri: string;
  returnTo: string; // pathname+search to navigate to after successful exchange
  ts: number;
}

/**
 * Kick off a PKCE auth code flow. Stores the verifier + return-to path
 * keyed by state, then redirects the browser to Cognito Hosted UI.
 * Resolves only if the redirect fails — normally never returns.
 */
export async function redirectToHostedUi(returnTo: string = '/home'): Promise<void> {
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  const state = generateStateValue();
  // Bind state → verifier so /callback can recover it.
  const pending: PendingAuth = {
    codeVerifier,
    redirectUri: `${window.location.origin}/callback`,
    returnTo,
    ts: Date.now(),
  };
  sessionStorage.setItem(STATE_KEY_PREFIX + state, JSON.stringify(pending));

  const url = new URL(`${COGNITO_HOSTED_UI}/oauth2/authorize`);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('client_id', CLIENT_ID);
  url.searchParams.set('redirect_uri', pending.redirectUri);
  url.searchParams.set('scope', 'openid email profile');
  url.searchParams.set('state', state);
  url.searchParams.set('code_challenge', codeChallenge);
  url.searchParams.set('code_challenge_method', 'S256');
  // Force Cognito to always show the login UI on /authorize, even when a
  // session cookie survives /logout (CDN cache, device-remember, browser
  // privacy modes). Without this, a leftover auth.f2-tech.ai session
  // cookie causes silent 302-with-code re-auth — the user clicks "Sign
  // out" and lands back logged in. Verified via F12 trace 2026-06-10:
  // /authorize with no prompt= returns 302 (silent re-auth) when a stale
  // Cognito session is present. Admin panel UX is deliberate sign-in;
  // there's no value in SSO bouncing here.
  url.searchParams.set('prompt', 'login');

  window.location.assign(url.toString());
}

/**
 * Recover the pending auth attempt by state value. Returns null if no
 * match (suggests CSRF, replay, or stale tab). Caller MUST treat null
 * as a hard failure and re-redirect to Hosted UI.
 */
export function consumePendingAuth(state: string): PendingAuth | null {
  const key = STATE_KEY_PREFIX + state;
  const raw = sessionStorage.getItem(key);
  if (!raw) return null;
  sessionStorage.removeItem(key); // one-shot
  try {
    return JSON.parse(raw) as PendingAuth;
  } catch {
    return null;
  }
}

/** Build the Hosted UI /logout URL — Cognito clears its own session cookie. */
export function hostedUiLogoutUrl(logoutUri: string): string {
  const url = new URL(`${COGNITO_HOSTED_UI}/logout`);
  url.searchParams.set('client_id', CLIENT_ID);
  url.searchParams.set('logout_uri', logoutUri);
  return url.toString();
}
